import './dealer-locator.scss';
import { DealerLocatorMap } from './dealer-locator-map';
import AjaxLoad from './../../utilities/js/ajax-load';
import { smoothScrollTo, getOffset, createElementFromHTML } from '../../utilities/js/helper';
import { Loader } from '@googlemaps/js-api-loader';
import Swiper from 'swiper';
import { Pagination, FreeMode, Mousewheel } from 'swiper/modules';

class DealerLocator {
    constructor ($container) {
        this.$dealerLocator = $container;

        this.$form = this.$dealerLocator.querySelector('form');
        this.$locationInput = this.$dealerLocator.querySelector('[data-dealer-locator="location"]');
        this.$inputDeleteButton = this.$dealerLocator.querySelector('[data-dealer-locator="delete-input"]');
        this.lastLocationValue = '';
        this.autoScroll = false;
        this.$submitButton = this.$dealerLocator.querySelector('[data-form-submit]');
        this.inputLAT = this.$dealerLocator.querySelector('[data-location="lat"]');
        this.inputLNG = this.$dealerLocator.querySelector('[data-location="lng"]');
        this.inputFilter = this.$dealerLocator.querySelector('[data-location="filter"]');
        this.inputCountry = this.$dealerLocator.querySelector('[data-location="country"]');
        this.$filterRoot = this.$dealerLocator.querySelector('[data-dealer-filter="root"]');
        this.$filter = this.$filterRoot?.querySelectorAll('[data-dealer-filter="filter"]');
        this.foundEvent = new CustomEvent('changeInput', {
            view: window,
            bubbles: true,
            cancelable: false
        });
        this.$noJobsLabel = this.$dealerLocator.querySelector('[data-dealer-locator="nojobsfound"]');
        this.autocomplete = null;
        this.$noResult = this.$dealerLocator.querySelector('[data-dealer-noresult]');
        this.$dealerResult = document.querySelector('[data-dealer-locator="results"]');
        this.$dealerResultItems = this.$dealerResult.querySelectorAll('[data-dealer-locator="dealer"]');
        this.$radius = document.querySelector('[data-select][id="radius"]');

        this.$dealerResultText = this.$dealerLocator.querySelector('[data-dealer-locator="resulttext"]');
        this.$dealerResultTextWrapper = this.$dealerLocator.querySelector('[data-dealer-resulttext-wrapper]');
        this.$dealerLocatorMap = this.$dealerLocator.querySelector('[data-dealer-locator="map"]');
        this.$dealerLocatorMapContainer = this.$dealerLocator.querySelector('[data-dealer-locator="mapcontainer"]');

        this.dealerMap = null;

        this.foundLocation = false;
        window.dealerSlider = null;

        this.activeRequest = false;

        this.googleAPIKey = googleMapsApiKey; // eslint-disable-line
    }

    initialize () {
        if (this.$dealerLocatorMap && this.googleAPIKey !== '') {
            const loader = new Loader({
                apiKey: this.googleAPIKey,
                version: 'quarterly',
                libraries: ['places']
            });

            loader.load().then(() => {
                if (typeof google !== 'undefined') {
                    this.dealerMap = new DealerLocatorMap(this.$dealerLocatorMap, {
                        centerLat: this.$dealerLocatorMap.getAttribute('data-lat'),
                        centerLng: this.$dealerLocatorMap.getAttribute('data-lng')
                    });

                    this.setEvents();
                    this.autoComplete();
                    this.dealerMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
                } else {
                    const $waitForGoogle = setInterval(() => {
                        if (typeof google !== 'undefined') {
                            clearInterval($waitForGoogle);
                            this.dealerMap = new DealerLocatorMap(this.$dealerLocatorMap, this, {
                                centerLat: this.$dealerLocatorMap.getAttribute('data-lat'),
                                centerLng: this.$dealerLocatorMap.getAttribute('data-lng')
                            });

                            this.setEvents();
                            this.autoComplete();
                            this.dealerMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
                        }
                    }, 100);
                }
            });
        }

        if (this.$dealerResultItems) {
            if (this.$dealerResultItems.length > 0) {
                this.initSlider();
            }
        }
    }

    scrollTo ($target) {
        smoothScrollTo(0, getOffset($target).top, 500);
    }

    initSlider () {
        this.$dealerResult = document.querySelector('[data-dealer-locator="results"]');
        this.$dealerResultItems = this.$dealerResult.querySelectorAll('[data-dealer-locator="dealer"]');

        window.dealerSlider = new Swiper(this.$dealerResult, {
            modules: [Pagination, FreeMode, Mousewheel],
            slidesPerView: 'auto',
            direction: 'horizontal',
            mousewheel: {
                forceToAxis: true
            },
            watchSlidesProgress: true,
            freeMode: {
                enabled: true,
                sticky: true
            },
            spaceBetween: 0,
            pagination: {
                el: '.dealer-locator__result-pagination',
                type: 'progressbar'
            },
            breakpoints: {
                1024: {
                    direction: 'vertical',
                    freeMode: {
                        enabled: true,
                        sticky: true
                    }
                }
            }
        });

        this.setSliderEvents();
    }

    setSliderEvents () {
        this.$dealerResultItems.forEach((dealer) => {
            const id = parseInt(dealer.getAttribute('data-dealer-id'));

            dealer.addEventListener('click', () => {
                dealer.classList.add('is--active');
                this.dealerMap.setActiveMarkerWithId(id);
            });
        });
    }

    destroySlider (callback) {
        if (window.dealerSlider !== null) {
            window.dealerSlider.destroy(true, true);
            window.dealerSlider = null;

            callback();
        } else {
            callback();
        }
    }

    dispatchForCode (event, callback) {
        let code;

        if (event.key !== undefined) {
            code = event.key;
        } else if (event.keyIdentifier !== undefined) {
            code = event.keyIdentifier;
        } else if (event.keyCode !== undefined) {
            code = event.keyCode;
        }

        callback(code);
    }

    autoComplete () {
        this.autocomplete = new google.maps.places.Autocomplete( // eslint-disable-line
            /** @type {!HTMLInputElement} */ (
                this.$locationInput), {
                types: ['(regions)']
            });

        this.autocomplete.addListener('place_changed', () => {
            const place = this.autocomplete.getPlace();

            const filteredArray = place.address_components.filter((address_component) => { // eslint-disable-line
                return address_component.types.includes('country');
            });
            const country = filteredArray.length ? filteredArray[0].short_name: ''; // eslint-disable-line

            if (place.geometry !== undefined) {
                if (this.inputLAT && this.inputLNG) {
                    this.inputLAT.value = place.geometry.location.lat();
                    this.inputLNG.value = place.geometry.location.lng();
                }

                if (country) {
                    this.inputCountry.value = country;
                }

                this.foundLocation = true;
                this.lastLocationValue = encodeURI(this.$locationInput.value);
                this.$form.dispatchEvent(this.foundEvent);
            } else {
                this.foundLocation = false;
            }
        });

        google.maps.event.addDomListener(this.$locationInput, 'keydown', (e) => { // eslint-disable-line
            if (!document.querySelector('.pac-item-selected')) {
                this.dispatchForCode(e, (code) => {
                    if (code === 'Enter' && !e.triggered) {
                        this.selectFirstLocation(true);
                    }
                });
            }
        });

        this.$form.addEventListener('changeInput', (e) => {
            this.checkBeforeSubmit(e);
        });
    }

    setEvents () {
        this.$radius.addEventListener('change', () => {
            if (this.$locationInput.value !== '') {
                this.$form.dispatchEvent(this.foundEvent);
            } else {
                this.ajaxRequest(this.$form.getAttribute('action'), false);
                this.autoscroll = false;
            }
        });

        if (this.$filter.length > 0) {
            this.$filter.forEach((filter) => {
                filter.addEventListener('change', () => {
                    this.$form.querySelector('input[type="checkbox"][name="filter-status-' + filter.getAttribute('name') + '"]').checked = filter.checked;
                    this.checkBeforeSubmit();
                });
            });
        }

        this.$inputDeleteButton.addEventListener('click', () => {
            this.$locationInput.value = '';
            this.$inputDeleteButton.classList.remove('is--visible');
        });

        this.$locationInput.addEventListener('keyup', () => {
            if (this.$locationInput.value.length >= 1) {
                this.$inputDeleteButton.classList.add('is--visible');
            } else {
                this.$inputDeleteButton.classList.remove('is--visible');
            }
        });

        this.$form.addEventListener('submit', (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.checkBeforeSubmit(e);
        });
    }

    checkBeforeSubmit (event) {
        if (encodeURI(this.$locationInput.value) !== this.lastLocationValue || this.foundLocation === false) {
            if (document.querySelectorAll('.pac-item').length > 0) {
                this.selectFirstLocation(true);
            }
        } else {
            if (event) {
                if (window.innerWidth <= 1024 && event.isTrusted) {
                    this.autoScroll = true;
                }
            }

            this.ajaxRequest(this.$form.getAttribute('action'), false);
            this.autoscroll = false;
        }
    }

    selectFirstLocation (withEnter) {
        let evt;
        if (document.documentElement.classList.contains('ie11')) {
            evt = document.createEvent('KeyboardEvent');
            evt.initKeyboardEvent('keydown', true, false, window, 'Down', 0, 0, 0, 0);
            this.$locationInput.dispatchEvent(evt);

            if (withEnter) {
                evt.initKeyboardEvent('Enter', true, false, window, 'Enter', 0, 0, 0, 0);
                this.$locationInput.dispatchEvent(evt);
            }
        } else if (document.documentElement.classList.contains('no-csshover')) {
            const Event1 = new Event('keydown');
            Event1.code = 'ArrowDown';
            Event1.key = 'ArrowDown';
            Event1.keyCode = 40;
            google.maps.event.trigger(this.$locationInput, 'keydown', Event1); // eslint-disable-line

            if (withEnter) {
                const Event2 = new Event('keydown');
                Event2.code = 'Enter';
                Event2.key = 'Enter';
                Event2.keyCode = 13;
                Event2.triggered = true;
                google.maps.event.trigger(this.$locationInput, 'keydown', Event2); // eslint-disable-line
            }
        } else {
            evt = new KeyboardEvent('keydown', { keyCode: 40, which: 40, code: 'ArrowDown' });
            this.$locationInput.dispatchEvent(evt);

            if (withEnter) {
                this.$locationInput.dispatchEvent(new KeyboardEvent('keydown', { keyCode: 13, which: 13, code: 'Enter' }));
            }
        }
    }

    ajaxRequest (url, scroll) {
        if (this.activeRequest === false) {
            this.activeRequest = true;

            this.checkFilter();

            new AjaxLoad(url, { // eslint-disable-line
                requestMethod: 'POST',
                requestData: new FormData(this.$form),
                onSuccess: (result) => {
                    const data = JSON.parse(result);

                    if (data.marker) {
                        this.$dealerLocator.classList.remove('has--no-results');
                        this.setResult(data, scroll);
                    } else {
                        this.$dealerLocator.classList.add('has--no-results');
                        this.setNoResult(data);
                    }

                    this.activeRequest = false;
                }
            });
        }
    }

    setResult (data, scroll) {
        const markers = JSON.parse(data.marker);

        if (data.resultText) {
            this.setDealerResultText(data.resultText);
        }

        const dealerResults = createElementFromHTML(data.result);

        this.destroySlider(() => {
            const $swiperWrapper = this.$dealerResult.querySelector('.swiper-wrapper');

            if ($swiperWrapper) {
                $swiperWrapper.remove();
            }

            this.$dealerResult.appendChild(dealerResults);
        });

        if (scroll) {
            this.scrollTo(this.$dealerResult);
        }

        this.initSlider();

        this.dealerMap.renewMarker(markers);
        this.dealerMap.setVisualRadius(this.inputLAT.value, this.inputLNG.value, this.$radius.value);
    }

    checkFilter () {
        if (this.inputCountry.value === 'GB') {
            this.$filterRoot.classList.remove('is--hidden');
        } else {
            this.$filter.forEach((filter) => {
                if (filter.checked) {
                    filter.nextElementSibling.click();
                }
            });

            this.$filterRoot.classList.add('is--hidden');
        }
    }

    setNoResult (data) {
        const markers = JSON.parse('[]');
        this.$noResult.innerHTML = '';

        const dealerNoResults = createElementFromHTML(data.result);
        this.$noResult.appendChild(dealerNoResults);

        this.destroySlider(() => {
            this.$dealerResult.querySelector('.swiper-wrapper').remove();
        });

        this.dealerMap.renewMarker(markers);
        this.dealerMap.removeVisualRadius();
    }

    setDealerResultText (resultText) {
        this.$dealerResultTextWrapper.classList.remove('is--hidden');
        this.$dealerResultText.innerHTML = resultText;
    }
}

export { DealerLocator };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $dealerLocator = $context.querySelector('[data-dealer-locator="root"]');
        if ($dealerLocator) {
            const dealers = new DealerLocator($dealerLocator);
            dealers.initialize();
        }
    }
});
